import { useMutation, useQuery } from '@apollo/client/react';
import { PageLoader } from '@optools/components/loading/page-loader';
import { SheetDescription, SheetTitle } from '@optools/components/ui/sheet';
import {
  DELETE_AUDIENCE,
  UPDATE_AUDIENCE,
} from '@optools/services/operator-gateway/esni/mutations';
import { AUDIENCE, GET_AUDIENCES } from '@optools/services/operator-gateway/esni/queries';
import {
  AudienceQuery,
  AudiencesQuery,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useState } from 'react';
import { toast } from 'sonner';

import { AudienceForm, AudienceFormValues } from './audience-form';

export interface AudienceDetailProps {
  audienceHref: string;
  onOpenChange: ((open: boolean) => void) | undefined;
}

export function AudienceDetail({ audienceHref, onOpenChange }: AudienceDetailProps) {
  const [canEdit, setCanEdit] = useState(false);

  const {
    data: audienceData,
    error: audienceQueryError,
    loading: audienceLoading,
  } = useQuery(AUDIENCE, {
    variables: { href: audienceHref },
  });

  const [updateAudience, { loading: loadingUpdateAudience }] = useMutation(UPDATE_AUDIENCE, {
    onCompleted: ({ updateAudience }) => {
      if (!updateAudience) {
        toast.error('Failed to update audience', {
          description: `Failed to update audience: ${audienceHref}`,
          duration: Infinity,
        });
        return;
      }

      toast.success('Audience successfully updated', {
        description: `Updated audience ${updateAudience?.href}`,
      });

      setCanEdit(false);
    },
    update: (cache, { data }) => {
      if (!data?.updateAudience) return;

      const audiencesQuery = cache.readQuery<AudiencesQuery>({
        query: GET_AUDIENCES,
      });

      if (!audiencesQuery) return;

      const updatedAudiences = audiencesQuery?.audiences?.map((item) => {
        return item.href === data?.updateAudience?.href ? data.updateAudience : item;
      });

      cache.writeQuery<AudiencesQuery>({
        data: { audiences: updatedAudiences },
        query: GET_AUDIENCES,
      });

      const audienceQuery = cache.readQuery<AudienceQuery>({
        query: AUDIENCE,
        variables: { href: audienceHref },
      });

      if (!audienceQuery) return;

      cache.writeQuery<AudienceQuery>({
        data: {
          audience: { ...audienceQuery.audience, ...data.updateAudience },
        },
        query: AUDIENCE,
        variables: { href: audienceHref },
      });
    },
  });

  const [deleteAudience, { loading: loadingDeleteAudience }] = useMutation(DELETE_AUDIENCE, {
    onCompleted: ({ deleteAudience }) => {
      const { href, reason, success } = deleteAudience;
      if (success) {
        toast.success('Audience successfully deleted', {
          description: `Deleted ${deleteAudience?.href} audience`,
        });
        onOpenChange?.(false);
      } else {
        toast.error('Failed to delete audience', {
          description: `Failed to delete audience ${href} due to: ${reason}`,
        });
      }
    },

    update: (cache, { data }) => {
      if (!data?.deleteAudience.success) return;

      const audiencesQuery = cache.readQuery<AudiencesQuery>({
        query: GET_AUDIENCES,
      });

      if (!audiencesQuery) return;

      const { href } = data.deleteAudience;

      cache.modify<AudiencesQuery>({
        fields: {
          audiences(existing, { readField }) {
            return existing.filter((item) => href !== readField('href', item));
          },
        },
      });
    },
  });

  const handleOnCancel = () => {
    setCanEdit(false);
  };

  const handleSubmit = (values: AudienceFormValues) => {
    const { href, ...audience } = values;
    updateAudience({ variables: { audience, href } });
  };

  const handleDelete = (href: string) => {
    deleteAudience({ variables: { href } });
  };

  return (
    <>
      <SheetTitle className="mb-6">Audience</SheetTitle>
      <SheetDescription className="sr-only">Update an audience</SheetDescription>
      {audienceQueryError ? (
        <p className="text-lg">{`Failed to fetch audience href: ${audienceHref}`}</p>
      ) : audienceLoading ? (
        <PageLoader />
      ) : (
        <AudienceForm
          audience={audienceData?.audience}
          editMode={canEdit}
          loading={loadingUpdateAudience || loadingDeleteAudience}
          updateEditMode={setCanEdit}
          onCancel={handleOnCancel}
          onDelete={handleDelete}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}
